import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdStatsService from "../../../services/OrderingServices/AdStatsService";
// import FilterCard from "../component/FilterCard";
import { formatDate } from "../../../Components/Common/FormatDate";

const AdStatsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adsData, setAdsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  console.log("🚀 ~ AdStatsDashboard ~ adsData:", adsData);
  const [mostViewedAd, setMostViewedAd] = useState(null);
  const [mostActiveDevice, setMostActiveDevice] = useState(null);
  const [filters, setFilters] = useState({
    adId: "",
    macId: "",
    fromDate: "",
    toDate: "",
  });

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
      },
    },
    rows: { style: { fontSize: "0.9rem" } },
  };

  useEffect(() => {
    fetchAllStats();
  }, []);

  async function fetchAllStats() {
    try {
      setLoading(true);
      const [adsRes, mostViewedRes, mostActiveRes] = await Promise.all([
        AdStatsService.getAdStats(),
        AdStatsService.getMostViewedAd(),
        AdStatsService.getMostActiveDevice(),
      ]);
      console.log("🚀 ~ fetchAllStats ~ mostActiveRes:", mostActiveRes);
      console.log("🚀 ~ fetchAllStats ~ adsRes:", adsRes);

      setAdsData(
        Array.isArray(adsRes)
          ? adsRes
          : Array.isArray(adsRes.data)
          ? adsRes.data
          : [],
      );
      setMostViewedAd(mostViewedRes);
      setMostActiveDevice(mostActiveRes);
    } catch (error) {
      // toast.error("Failed to fetch ad statistics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleDateRangeChange = async () => {
    try {
      setLoading(true);
      const response = await AdStatsService.getAdStats({
        startDate: filters.fromDate,
        endDate: filters.toDate,
      });
      setAdsData(
        Array.isArray(response)
          ? response
          : Array.isArray(response.data)
          ? response.data
          : [],
      );
    } catch (error) {
      // toast.error("Failed to fetch ad statistics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      adId: "",
      macId: "",
      fromDate: "",
      toDate: "",
    });
    setCurrentPage(1);
    fetchAllStats();
  };

  const StatCard = ({ title, value, subtitle, color = "primary", icon }) => (
    <Card className="border-0 shadow-sm h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <span className="text-muted small d-block">{title}</span>
            <h4 className={`mt-2 text-${color}`}>{value ?? "-"}</h4>
            {subtitle && (
              <small className="text-muted d-block mt-1">{subtitle}</small>
            )}
          </div>
          {icon && (
            <i className={`bx ${icon} fs-3 text-${color} opacity-50`}></i>
          )}
        </div>
      </CardBody>
    </Card>
  );

  const topAdsColumns = [
    { name: "SN", selector: (row, index) => index + 1, width: "60px" },

    {
      name: "Ad Title",
      selector: (row) => row.adTitle || row.title || "-",
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => formatDate(new Date(row.date)) || "-",
      sortable: true,
    },
    {
      name: "Impressions",
      selector: (row) => row.totalImpressions || 0,
      sortable: true,
    },
    {
      name: "Completed Plays",
      selector: (row) => row.completedPlays || 0,
      sortable: true,
    },
    {
      name: "Avg Duration",
      selector: (row) => {
        const avgDuration =
          row.totalPlayedSeconds && row.totalImpressions
            ? Math.round(row.totalPlayedSeconds / row.totalImpressions)
            : 0;
        return `${avgDuration}s`;
      },
      sortable: true,
    },
    {
      name: "Completion Rate",
      selector: (row) => {
        const impressions = row.totalImpressions || 0;
        const completed = row.completedPlays || 0;
        const rate =
          impressions > 0 ? ((completed / impressions) * 100).toFixed(1) : 0;
        return <Badge color="info">{rate}%</Badge>;
      },
      sortable: true,
    },
    {
      name: "Unique Devices",
      selector: (row) => row.uniqueDevices || 0,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          color="info"
          outline
          size="sm"
          onClick={() => navigate(`/ads/stats/ad-summary/${row.adId}`)}
          title="View Details"
        >
          <i className="bx bx-detail"></i>
        </Button>
      ),
    },
  ];

  if (loading && adsData.length === 0) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}
      >
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Ad Daily Stats" pageTitle="Ads Management" />

          {/* Key Metrics Row */}
          <Row className="mb-4">
            <Col md="3">
              <StatCard
                title="Total Views"
                value={adsData.reduce(
                  (sum, ad) => sum + (ad.totalImpressions || 0),
                  0,
                )}
                icon="bx-eye"
                color="primary"
              />
            </Col>
            <Col md="3">
              <StatCard
                title="Total Completions"
                value={adsData.reduce(
                  (sum, ad) => sum + (ad.completedPlays || 0),
                  0,
                )}
                icon="bx-check-circle"
                color="success"
              />
            </Col>
            <Col md="3">
              <StatCard
                title="Total Seconds Played"
                value={Math.round(
                  adsData.reduce(
                    (sum, ad) => sum + (ad.totalPlayedSeconds || 0),
                    0,
                  ),
                )}
                icon="bx-time"
                color="warning"
              />
            </Col>
            <Col md="3">
              <StatCard
                title="Unique Devices"
                value={adsData.reduce(
                  (sum, ad) => sum + (ad.uniqueDevices || 0),
                  0,
                )}
                icon="bx-devices"
                color="info"
              />
            </Col>
          </Row>

          {/* Most Viewed and Most Active Row */}
          <Row className="">
            <Col md="6">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <Row className="">
                    <Col md="6">
                      <h5 className="mb-0">Most Viewed Ad</h5>
                    </Col>

                    <Col md="6">
                      <div className="d-flex justify-content-end">
                        {" "}
                        <Button
                          color="info"
                          outline
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/ads/stats/ad-summary/${
                                mostViewedAd.adId || mostViewedAd.id
                              }`,
                            )
                          }
                        >
                          View Details
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {mostViewedAd ? (
                    <div>
                      <Row>
                        <Col md="4">
                          <p className="text-muted small mb-1">Ad ID</p>
                          <h5>{mostViewedAd.adId || mostViewedAd.id || "-"}</h5>
                        </Col>
                        <Col md="4">
                          <p className="text-muted small mb-1">
                            Total Impressions
                          </p>
                          <h5 className="text-primary">
                            {mostViewedAd.totalImpressions ||
                              mostViewedAd.views ||
                              0}
                          </h5>
                        </Col>
                        <Col md="4">
                          <p className="text-muted small mb-1">Title</p>
                          <p className="mb-2">
                            {mostViewedAd.adTitle || mostViewedAd.title || "-"}
                          </p>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <p className="text-muted">No data available</p>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md="6">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <Row>
                    <Col>
                      <h5 className="mb-0">Most Active Device</h5>
                    </Col>
                    <Col>
                      <div className="d-flex justify-content-end">
                        {" "}
                        <Button
                          color="success"
                          outline
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/ads/stats/device/${mostActiveDevice.macId}`,
                            )
                          }
                        >
                          View Device Stats
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {mostActiveDevice ? (
                    <div>
                      <Row>
                        <Col md="4">
                          <p className="text-muted small mb-1">Device Name</p>
                          <h5>{mostActiveDevice.deviceName || "-"}</h5>
                        </Col>
                        <Col md="4">
                          <p className="text-muted small mb-1">Total Views</p>
                          <h5 className="text-success">
                            {mostActiveDevice.views ||
                              mostActiveDevice.adsPlayed ||
                              0}
                          </h5>
                        </Col>
                        <Col md="4">
                          <p className="text-muted small mb-1">MAC ID</p>
                          <p className="mb-2 font-monospace">
                            {mostActiveDevice.macId || "-"}
                          </p>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <p className="text-muted">No data available</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Filter Card */}
          {/* <FilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            setCurrentPage={setCurrentPage}
          /> */}

          {/* Top Ads Section */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">Overall Ads</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Triangle color="#435ebe" height={80} width={80} />
                    </div>
                  ) : (
                    <DataTable
                      columns={topAdsColumns}
                      data={adsData}
                      pagination
                      customStyles={customStyles}
                      noDataComponent={
                        <div style={{ padding: "20px" }}>
                          No ads data available
                        </div>
                      }
                      defaultSortFieldId="totalImpressions"
                      defaultSortAsc={false}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdStatsDashboard;
